import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../context/AppContext";
import { useCopy } from "../../context/CopyContext";
import Keyboard from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";
import styles from "./EmailCaptureView.module.css";
import Header from "../Header";
import Heading from "../Heading";
import Button from "../Button";
import PoweredBy from "../PoweredBy";
import ItemsModalView from "./ItemsModalView";

const EmailCaptureView: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const { getCopy } = useCopy();
  const [email, setEmail] = useState(state.userEmail || "");
  const [isValid, setIsValid] = useState(true);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [layoutName, setLayoutName] = useState("default");
  const [showItemsModal, setShowItemsModal] = useState(false);
  const keyboardRef = useRef<any>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputRefNoKeyboard = useRef<HTMLInputElement>(null);
  const keyboardContainerRef = useRef<HTMLDivElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);

  // Mock email checking - simulate checking against a database
  const checkEmailExists = (emailAddress: string): boolean => {
    // Mock logic: emails ending with @brand.com or @pentatonic.com are "existing members"
    const existingDomains = ["@brand.com", "@pentatonic.com"];
    return existingDomains.some(domain => emailAddress.toLowerCase().includes(domain));
  };

  const validateEmail = (emailAddress: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailAddress);
  };

  const handleBack = () => {
    dispatch({ type: 'SET_ITEM_DISPENSED', payload: true });
    navigate("/place-empty", { state: { fromEmailCapture: true } });
  };

  const handleItemsClick = () => {
    setShowItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setShowItemsModal(false);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    // Update keyboard input
    if (keyboardRef.current) {
      keyboardRef.current.setInput(newEmail);
    }
    
    // Only validate email format after @ symbol is entered
    if (newEmail.includes('@')) {
      setIsValid(validateEmail(newEmail));
    } else {
      setIsValid(true);
    }
  };

  const onKeyboardChange = (input: string) => {
    setEmail(input);
    
    // Only validate email format after @ symbol is entered
    if (input.includes('@')) {
      setIsValid(validateEmail(input));
    } else {
      setIsValid(true);
    }
  };

  const onKeyPress = (button: string) => {
    if (button === "{shift}" || button === "{lock}") {
      setLayoutName(layoutName === "default" ? "shift" : "default");
    }
    
    if (button === "{enter}") {
      handleSubmit(new Event('submit') as any);
    }
  };

  const onInputFocus = () => {
    if (!showKeyboard) {
      setShowKeyboard(true);
    }
  };

  const onInputBlur = () => {
    // Small delay to prevent keyboard from hiding when clicking on it
    setTimeout(() => {
      const currentActiveInput = showKeyboard ? inputRef.current : inputRefNoKeyboard.current;
      if (document.activeElement !== currentActiveInput) {
        setShowKeyboard(false);
      }
    }, 100);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setIsValid(false);
      return;
    }

    // Only validate if @ symbol is present
    if (email.includes('@') && !validateEmail(email)) {
      setIsValid(false);
      return;
    }

    // Store email in state
    dispatch({ type: "SET_EMAIL", payload: email });
    
    // Mock email checking logic
    const emailExists = checkEmailExists(email);
    
    if (emailExists) {
      // Existing member - navigate to member flow
      console.log("Existing member detected:", email);
      dispatch({ type: "SET_MEMBER", payload: true });
      dispatch({ type: "SET_GUEST", payload: false });
      dispatch({ type: "SET_IS_SIGNUP", payload: false }); // This is a login flow
      navigate("/member-welcome"); // This would be the next view for existing members
    } else {
      // Unrecognized email - navigate to membership selection
      console.log("Unrecognized email:", email);
      dispatch({ type: "SET_GUEST", payload: true }); // Set as guest initially
      navigate("/membership-selection");
    }
  };

  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as Node;
    
    // Check if click is outside both the input and keyboard areas
    const isOutsideInput = inputContainerRef.current && !inputContainerRef.current.contains(target);
    const isOutsideKeyboard = keyboardContainerRef.current && !keyboardContainerRef.current.contains(target);
    const currentActiveInput = showKeyboard ? inputRef.current : inputRefNoKeyboard.current;
    const isOutsideInputField = currentActiveInput && !currentActiveInput.contains(target);
    
    if (showKeyboard && isOutsideInput && isOutsideKeyboard && isOutsideInputField) {
      setShowKeyboard(false);
      if (currentActiveInput) {
        currentActiveInput.blur();
      }
    }
  }, [showKeyboard]);

  useEffect(() => {
    if (showKeyboard) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showKeyboard, handleClickOutside]);

  // Initialize keyboard with current email value when keyboard is shown
  useEffect(() => {
    if (showKeyboard && email) {
      // Small delay to ensure keyboard component is fully rendered
      const timer = setTimeout(() => {
        if (keyboardRef.current) {
          keyboardRef.current.setInput(email);
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [showKeyboard, email]);

  return (
    <div
      className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden"
      data-node-id="104:708"
    >
      {/* Content Container */}
      <div className="relative z-10 flex-1 flex flex-col gap-24">
        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={state.itemsCount > 0}
              itemsNumber={state.itemsCount.toString()}
              onItemsClick={handleItemsClick}
            />
          </div>
        </div>

        {/* Main Content Area - Dynamic layout based on keyboard state */}
        <motion.div 
          className={`flex-1 grid gap-0 mx-20 ${showKeyboard ? 'grid-rows-2' : 'grid-rows-3'}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Row 1 - Heading (spans 2 rows when no keyboard, 1 row when keyboard shown) */}
          <div className={`${showKeyboard ? '' : 'row-span-2'} flex items-center justify-center`}>
            <div className="text-center w-full max-w-[920px]">
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ 
                  duration: 0.6, 
                  ease: [0.25, 0.1, 0.25, 1]
                }}
                layout
              >
                <Heading 
                  title={getCopy('emailCapture.heading')}
                  subtitle={getCopy('emailCapture.subheading')}
                />
              </motion.div>
            </div>
          </div>

          {/* Row 2 - Email input (only appears when keyboard is shown) */}
          {showKeyboard && (
            <div className="flex items-start justify-center" ref={inputContainerRef}>
              <motion.form
                className="w-full max-w-[920px]"
                onSubmit={handleSubmit}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{
                  duration: 0.6,
                  delay: 0.2,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              >
                <div className="relative">
                  <input
                    ref={inputRef}
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    onFocus={onInputFocus}
                    onBlur={onInputBlur}
                    placeholder={getCopy('emailCapture.placeholder')}
                    className={`w-full h-[88px] px-6 text-[32px] leading-[58px] font-light tracking-[-0.8px] text-center border-0 border-b-2 bg-transparent outline-none transition-colors duration-200 ${
                      !isValid 
                        ? "bg-brand-background border-red-500 text-red-500 placeholder-red-300" 
                        : "bg-brand-background border-brand-text-primary text-brand-text-primary placeholder-brand-text-muted focus:border-brand-primary"
                    }`}
                    style={{
                      fontFamily: "var(--brand-font-primary), sans-serif",
                    }}
                  />
                  {!isValid && (
                    <motion.p
                      className="text-red-500 text-center mt-2 text-lg"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      {getCopy('emailCapture.errorMessage')}
                    </motion.p>
                  )}
                </div>
              </motion.form>
            </div>
          )}

          {/* Row 3 - Email input (only appears when no keyboard) */}
          {!showKeyboard && (
            <div className="flex items-end justify-center" ref={inputContainerRef}>
              <motion.form
                className="w-full max-w-[920px]"
                onSubmit={handleSubmit}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  delay: 0.2,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              >
                <div className="relative">
                  <input
                    ref={inputRefNoKeyboard}
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    onFocus={onInputFocus}
                    onBlur={onInputBlur}
                    placeholder={getCopy('emailCapture.placeholder')}
                    className={`w-full h-[88px] px-6 text-[32px] leading-[58px] font-light tracking-[-0.8px] text-center border-0 border-b-2 bg-transparent outline-none transition-colors duration-200 ${
                      !isValid 
                        ? "bg-brand-background border-red-500 text-red-500 placeholder-red-300" 
                        : "bg-brand-background border-brand-text-primary text-brand-text-primary placeholder-brand-text-muted focus:border-brand-primary"
                    }`}
                    style={{
                      fontFamily: "var(--brand-font-primary), sans-serif",
                    }}
                  />
                  {!isValid && (
                    <motion.p
                      className="text-red-500 text-center mt-2 text-lg"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      {getCopy('emailCapture.errorMessage')}
                    </motion.p>
                  )}
                </div>
              </motion.form>
            </div>
          )}
        </motion.div>

        {/* Bottom Section - Back button and footer (hidden when keyboard is shown) */}
        {!showKeyboard && (
          <div className="flex flex-col gap-16 items-center mx-20 mb-10">
            {/* Buttons */}
            <div className={`flex flex-row gap-6 lg:gap-10 w-full ${!state.userEmail ? 'justify-center' : ''}`}>
              <Button 
                variant={state.userEmail ? "secondary" : "tertiary"} 
                onClick={handleBack}
                className={!state.userEmail ? 'flex-none w-auto' : ''}
              >
                {getCopy('emailCapture.buttons.tertiary')}
              </Button>
              {state.userEmail && (
                <Button variant="primary" onClick={() => handleSubmit(new Event('submit') as any)}>
                  {getCopy('emailCapture.buttons.primary')}
                </Button>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-center">
              <PoweredBy />
            </div>
          </div>
        )}
      </div>

      {/* Virtual Keyboard - Hugs the height of keyboard keys */}
      <AnimatePresence>
        {showKeyboard && (
          <motion.div
            className="fixed bottom-0 left-0 right-0 z-50 bg-brand-background"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{
              duration: 0.3,
              ease: [0.25, 0.1, 0.25, 1],
            }}
            ref={keyboardContainerRef}
          >
            <div className={styles.keyboardContainer}>
              <Keyboard
                ref={keyboardRef}
                layoutName={layoutName}
                onChange={onKeyboardChange}
                onKeyPress={onKeyPress}
                theme="hg-theme-default hg-layout-default"
                layout={{
                  default: [
                    "1 2 3 4 5 6 7 8 9 0 {bksp}",
                    "q w e r t y u i o p",
                    "a s d f g h j k l",
                    "{shift} z x c v b n m {shift}",
                    "@ .com {enter}"
                  ],
                  shift: [
                    "! @ # $ % ^ & * ( ) {bksp}",
                    "Q W E R T Y U I O P",
                    "A S D F G H J K L",
                    "{shift} Z X C V B N M {shift}",
                    "@ .com {enter}"
                  ]
                }}
                display={{
                  '{bksp}': '⌫',
                  '{enter}': 'Continue',
                  '{shift}': '⇧',
                  '{space}': ' ',
                  '@': '@',                  
                  '.com': '.com'
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Items Modal */}
      <ItemsModalView 
        isOpen={showItemsModal} 
        onClose={handleCloseItemsModal} 
      />
    </div>
  );
};

export default EmailCaptureView;
